/** Narrow projection from the reviewed MRC-only Portal RPC. No NetOps API client. */
export const MRC_INCIDENT_LOCATION = 'caccbf7a-2b40-4d45-b9ff-5fe27c924a8c';
const kinds = ['collector_heartbeat_unavailable', 'collector_spool_pressure', 'source_unavailable'] as const;
const states = ['pending', 'firing', 'recovering', 'resolved'] as const;
const severities = ['info', 'warning', 'critical'] as const;
const sources = ['Collector', 'SNMP', 'UniFi', 'Flow', 'DNS', 'Collection source'] as const;
export interface MrcCollectorIncident {
  incident_id: string;
  kind: typeof kinds[number];
  state: typeof states[number];
  severity: typeof severities[number];
  source_label: typeof sources[number];
  opened_at: string;
  incident_updated_at: string;
  rule_record_updated_at: string;
  netops_acknowledged_at: string | null;
}
export interface MrcCollectorIncidents {
  schema_version: 1;
  location_id: typeof MRC_INCIDENT_LOCATION;
  mapping_version: number;
  source: 'PazNetOps';
  read_at: string;
  configured_rule_kinds: number;
  rule_record_updated_at: string | null;
  truncated: boolean;
  legacy_assessment_rows_not_included: number;
  acknowledgement_supported: false;
  incidents: MrcCollectorIncident[];
}
export type IncidentFailure = 'access' | 'unconfigured' | 'unavailable';
export class CollectorIncidentError extends Error {
  constructor(readonly kind: IncidentFailure) { super('Collector incident records unavailable'); }
}
function invalid(): never { throw new CollectorIncidentError('unavailable'); }
function object(value: unknown, names: readonly string[]): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || Object.keys(value).sort().join('|') !== [...names].sort().join('|')) return invalid();
  return value as Record<string, unknown>;
}
function timestamp(value: unknown, nullable = false): void {
  if (nullable && value === null) return;
  if (typeof value !== 'string' || value.length > 40
    || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,6})?(Z|\+00:00)$/.test(value)
    || !Number.isFinite(Date.parse(value))) invalid();
}
function integer(value: unknown, min: number, max: number): void {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < min || value > max) invalid();
}
function member(value: unknown, values: readonly string[]): void {
  if (typeof value !== 'string' || !values.includes(value)) invalid();
}
/** Fail closed on additional fields; raw evidence can never enter this view. */
export function parseMrcCollectorIncidents(value: unknown): MrcCollectorIncidents {
  const top = object(value, ['schema_version', 'location_id', 'mapping_version', 'source', 'read_at',
    'configured_rule_kinds', 'rule_record_updated_at', 'truncated', 'legacy_assessment_rows_not_included',
    'acknowledgement_supported', 'incidents']);
  if (top.schema_version !== 1 || top.location_id !== MRC_INCIDENT_LOCATION || top.source !== 'PazNetOps'
    || top.acknowledgement_supported !== false || typeof top.truncated !== 'boolean'
    || !Array.isArray(top.incidents) || top.incidents.length > 100) return invalid();
  integer(top.mapping_version, 1, Number.MAX_SAFE_INTEGER);
  integer(top.configured_rule_kinds, 0, 3);
  integer(top.legacy_assessment_rows_not_included, 0, Number.MAX_SAFE_INTEGER);
  timestamp(top.read_at); timestamp(top.rule_record_updated_at, true);
  const seen = new Set<string>();
  for (const value of top.incidents) {
    const row = object(value, ['incident_id', 'kind', 'state', 'severity', 'source_label', 'opened_at',
      'incident_updated_at', 'rule_record_updated_at', 'netops_acknowledged_at']);
    if (typeof row.incident_id !== 'string'
      || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(row.incident_id)
      || seen.has(row.incident_id)) return invalid();
    seen.add(row.incident_id);
    member(row.kind, kinds); member(row.state, states); member(row.severity, severities); member(row.source_label, sources);
    timestamp(row.opened_at); timestamp(row.incident_updated_at); timestamp(row.rule_record_updated_at);
    timestamp(row.netops_acknowledged_at, true);
  }
  // Every field and enum above is checked before narrowing the unknown response.
  return value as MrcCollectorIncidents;
}
